import { useState, useSyncExternalStore } from 'react'
import { Link } from 'react-router'
import { damageStage, type PinataRules } from '#app/utils/pinata/game.ts'
import { sfxMute } from '#app/utils/pinata/sfx.ts'
import { createPinataStore, type PinataStore } from '#app/utils/pinata/store.ts'
import { PinataStage } from './pinata-stage.tsx'

export type PinataPageData = {
	slug: string
	title: string
	imageSrc: string
	rules: PinataRules
	/** Changes whenever an admin saves, so an edit starts a fresh game */
	version: number
	/** Only set for admins */
	editUrl: string | null
}

export function getPinataImgSrc(objectKey: string) {
	return `/resources/images?objectKey=${encodeURIComponent(objectKey)}`
}

export function pinataMeta(pinata: PinataPageData | undefined) {
	const title = pinata ? `${pinata.title} - Whack the Piñata!` : 'Whack the Piñata!'
	const description = 'Grab a stick. Click or press Space to whack it until it bursts.'
	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		// These are private jokes sent by link; keep them out of search results.
		{ name: 'robots', content: 'noindex, nofollow' },
	]
}

export function PinataPage({ pinata }: { pinata: PinataPageData }) {
	// Remounts for a different piñata, or the same one after an admin edit.
	return <PinataGame key={`${pinata.slug}:${pinata.version}`} pinata={pinata} />
}

function PinataGame({ pinata }: { pinata: PinataPageData }) {
	const [store] = useState(() => createPinataStore(pinata.rules))
	const seed = hashSlug(pinata.slug)

	return (
		<div className="flex min-h-dvh flex-col bg-gradient-to-b from-indigo-950 via-purple-900 to-fuchsia-900 text-white">
			<header className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
				<Link
					to="/"
					className="text-sm text-white/70 underline-offset-4 hover:text-white hover:underline"
				>
					Provided by <span className="font-semibold">Jase Owns</span>
				</Link>
				<nav className="flex items-center gap-2">
					{pinata.editUrl ? (
						<Link
							to={pinata.editUrl}
							className="rounded-full border border-white/40 px-4 py-1.5 text-sm font-semibold hover:bg-white/10"
						>
							Edit
						</Link>
					) : null}
					<Link
						to="/support"
						className="rounded-full bg-amber-400 px-4 py-1.5 text-sm font-bold text-indigo-950 shadow hover:bg-amber-300"
					>
						Support
					</Link>
				</nav>
			</header>

			<Bunting />

			<main className="flex flex-1 flex-col gap-3 px-4 pb-6 sm:px-6">
				<div className="text-center">
					<h1 className="text-3xl font-black tracking-tight drop-shadow sm:text-5xl">
						{pinata.title}
					</h1>
					<p className="mt-1 text-sm text-white/70">
						Click the piñata or press{' '}
						<kbd className="rounded border border-white/40 px-1">Space</kbd>{' '}
						{pinata.rules.powerSwing
							? 'to whack it. Wait for the marker to hit the green for a critical hit!'
							: 'to whack it'}
					</p>
				</div>
				<PinataHud store={store} />
				<div className="relative min-h-[360px] w-full flex-1">
					<div className="absolute inset-0">
						<PinataStage imageSrc={pinata.imageSrc} store={store} seed={seed} />
					</div>
				</div>
			</main>
		</div>
	)
}

/** A string of papel-picado flags across the top. Decorative only. */
function Bunting() {
	const colors = ['#ff4d6d', '#ffd166', '#06d6a0', '#3ec1d3', '#8338ec', '#fb5607']
	return (
		<svg aria-hidden className="block h-7 w-full" preserveAspectRatio="none" viewBox="0 0 480 28">
			<path d="M0 3 Q240 12 480 3" stroke="#ffffff55" strokeWidth="1.5" fill="none" />
			{Array.from({ length: 24 }, (_, i) => {
				const x = i * 20
				// Follow the string's sag so the flags hang from it.
				const t = (x + 10) / 480
				const y = 3 + 2 * t * (1 - t) * 9
				return (
					<path
						key={i}
						d={`M${x + 2} ${y} L${x + 18} ${y} L${x + 10} ${y + 14} Z`}
						fill={colors[i % colors.length]}
						opacity="0.9"
					/>
				)
			})}
		</svg>
	)
}

function PinataHud({ store }: { store: PinataStore }) {
	const state = useSyncExternalStore(store.subscribe, store.getState, store.getState)
	const pct = Math.round((state.health / state.maxHealth) * 100)
	const exploded = state.status === 'exploded'
	const stage = damageStage(state)

	return (
		<div className="mx-auto flex w-full max-w-3xl flex-wrap items-center justify-center gap-x-4 gap-y-2">
			<div className="flex min-w-[220px] flex-1 items-center gap-3">
				<div
					className="h-4 flex-1 overflow-hidden rounded-full bg-white/15"
					role="progressbar"
					aria-label="Piñata health"
					aria-valuemin={0}
					aria-valuemax={state.maxHealth}
					aria-valuenow={state.health}
				>
					<div
						className={`h-full transition-[width] duration-150 ${
							stage >= 3 ? 'bg-red-500' : stage >= 1 ? 'bg-amber-400' : 'bg-emerald-400'
						}`}
						style={{ width: `${pct}%` }}
					/>
				</div>
				<span className="w-12 text-right tabular-nums">{pct}%</span>
			</div>
			<span className="tabular-nums" data-testid="pinata-hits">
				Hits: {state.hits}
			</span>
			{state.rules.powerSwing ? (
				<span className="tabular-nums" data-testid="pinata-power-stats">
					Crits: {state.crits} · Weak: {state.weakHits}
				</span>
			) : null}
			<span aria-live="polite" className="font-bold" data-testid="pinata-status">
				{exploded ? `Exploded in ${state.hits} hits!` : state.combo > 0 ? `Combo x${state.combo + 1}` : ''}
			</span>
			<MuteButton />
			{exploded ? (
				<button
					type="button"
					onClick={() => store.reset()}
					className="rounded-full bg-emerald-400 px-4 py-1.5 font-bold text-indigo-950 hover:bg-emerald-300"
				>
					Whack again
				</button>
			) : null}
		</div>
	)
}

function MuteButton() {
	const muted = useSyncExternalStore(sfxMute.subscribe, sfxMute.get, sfxMute.getServer)
	return (
		<button
			type="button"
			onClick={(e) => {
				sfxMute.set(!muted)
				// Hand Space back to the piñata instead of re-toggling this button.
				e.currentTarget.blur()
			}}
			aria-pressed={muted}
			className="rounded-full border border-white/40 px-3 py-1 text-sm hover:bg-white/10"
		>
			{muted ? '🔇 Sound off' : '🔊 Sound on'}
		</button>
	)
}

function hashSlug(slug: string) {
	let h = 2166136261
	for (let i = 0; i < slug.length; i++) h = Math.imul(h ^ slug.charCodeAt(i), 16777619)
	return h >>> 0
}
